// Masifi: reload an <audio>/<video> whose first fetch failed.
//
// Why: a message sent from the dashboard is broadcast (message.created, ActionCable) before ActiveStorage
// finishes uploading its file to S3 (the upload runs in the request's after_commit, ~0.5 s later). The bubble
// mounts, the browser follows the blob redirect to S3 and gets an error; <audio> never retries on its own, so
// the agent's own voice note showed 00:00 and would not play until a page reload. Upstream already retries
// images for the same reason (dashboard/composables/loadWithRetry.js); this is the media-element equivalent.
//
// Use: `@error="retryMediaLoad"` on the <source> (or on the media element when it carries `src` itself).
export const MEDIA_LOAD_RETRY = Object.freeze({ maxRetry: 5, backoff: 1000 });

const attempts = new WeakMap();

const isMediaElement = el => el instanceof HTMLMediaElement;

const mediaFor = target => {
  if (isMediaElement(target)) return target;
  if (target?.tagName === 'SOURCE' && isMediaElement(target.parentElement)) {
    return target.parentElement;
  }
  return null;
};

// New `t` so no cache (the 5-min cached blob redirect or the browser's media cache) replays the failed fetch.
const refreshTimestamp = el => {
  const src = el.getAttribute('src');
  if (!src) return;
  try {
    const url = new URL(src, window.location.href);
    if (!['http:', 'https:'].includes(url.protocol)) return;
    url.searchParams.set('t', Date.now());
    el.setAttribute('src', url.toString());
  } catch {
    /* leave an unparsable src alone */
  }
};

export const retryMediaLoad = event => {
  const target = event?.target;
  const media = mediaFor(target);
  if (!media) return false;

  const attempt = attempts.get(media) || 0;
  if (attempt >= MEDIA_LOAD_RETRY.maxRetry) return false;
  attempts.set(media, attempt + 1);

  setTimeout(
    () => {
      if (!media.isConnected) return;
      refreshTimestamp(target);
      media.load();
    },
    MEDIA_LOAD_RETRY.backoff * (attempt + 1)
  );
  return true;
};
